// AgentUiProvider — wrap app inside <Router>. Tạo store + executeViaForm,
// expose qua context. Auto-clear query params khi resolve/reject.

import { useNavigate, useSearchParams } from "@solidjs/router";
import { type ParentComponent } from "solid-js";
import { AgentUiContext } from "./context";
import { createExecuteViaForm } from "./execute-via-form";
import { createPendingAskUserStore, createPendingUiActionStore } from "./pending-store";
import type { AgentUiContextValue, PendingUiAction } from "./types";

export interface AgentUiProviderProps {
  /// Optional callback khi pending bị reject (tracking / analytics).
  onReject?: (reason: string, action: PendingUiAction) => void;
}

export const AgentUiProvider: ParentComponent<AgentUiProviderProps> = (props) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const store = createPendingUiActionStore();
  const askUserStore = createPendingAskUserStore();
  const executeViaForm = createExecuteViaForm(navigate, store);

  // Khi resolve/reject — clear query params (intent + prefill) để URL clean.
  const clearParams = () => {
    setSearchParams({ intent: undefined, prefill: undefined }, { replace: true });
  };

  const value: AgentUiContextValue = {
    pendingAction: store.current,
    executeViaForm,
    resolveCurrent: (result) => {
      store.resolve(result);
      clearParams();
    },
    rejectCurrent: (reason) => {
      const action = store.current();
      if (action && props.onReject) {
        try {
          props.onReject(reason, action);
        } catch {
          // Swallow.
        }
      }
      store.reject(reason);
      clearParams();
    },

    // ─── ask_user clarification ─────────────────────────────────
    pendingQuestion: askUserStore.current,
    askUser: (question) =>
      new Promise<string>((resolve, reject) => {
        askUserStore.replace({ question, resolve, reject });
      }),
    submitAnswer: (text) => {
      askUserStore.resolve(text);
    },
    cancelQuestion: (reason) => {
      askUserStore.reject(reason ?? "cancelled by user");
    },
  };

  return <AgentUiContext.Provider value={value}>{props.children}</AgentUiContext.Provider>;
};
